import type { StoreEvent } from '../data/events.model';
import { eventDayKeys, todayKey } from './event-format.util';

/**
 * Construcción de la rejilla mensual, sin librería de fechas.
 *
 * La aritmética del calendario se hace **en UTC sobre números** (`Date.UTC`), no
 * sobre instantes: una rejilla es un hecho del calendario civil —el 1 de junio
 * de 2027 cae en martes— y no depende de ninguna zona horaria. La zona sí
 * importa al decidir en qué casilla cae cada evento, y de eso se encarga
 * `eventDayKeys`, que resuelve los días en hora de Costa Rica.
 */

export interface CalendarDay {
  /** `YYYY-MM-DD`, la misma clave civil que emite `eventDayKeys`. */
  key: string;
  dayOfMonth: number;
  /** `false` en los días de relleno del mes anterior o siguiente. */
  inMonth: boolean;
  isToday: boolean;
  events: StoreEvent[];
}

const DAYS_PER_WEEK = 7;

function pad(value: number, length = 2): string {
  return String(value).padStart(length, '0');
}

function dayKeyOf(date: Date): string {
  return `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** Lunes = 0. `getUTCDay()` cuenta desde domingo, y la rejilla empieza en lunes. */
function mondayFirstIndex(date: Date): number {
  return (date.getUTCDay() + 6) % DAYS_PER_WEEK;
}

/**
 * Agrupa los eventos por día civil. Un evento de varios días aparece en **todas**
 * sus casillas: un torneo del 30 de junio al 2 de julio se ve los tres días, no
 * solo el de arranque.
 */
export function groupEventsByDay(events: readonly StoreEvent[]): Map<string, StoreEvent[]> {
  const byDay = new Map<string, StoreEvent[]>();

  for (const event of events) {
    for (const key of eventDayKeys(event.startsAt, event.endsAt)) {
      const bucket = byDay.get(key);
      if (bucket) {
        bucket.push(event);
      } else {
        byDay.set(key, [event]);
      }
    }
  }

  return byDay;
}

/**
 * Semanas (lunes a domingo) que cubren el mes, con sus eventos ya repartidos.
 * Se emiten solo las semanas necesarias —cuatro, cinco o seis— para no dejar una
 * fila vacía al final, que en móvil es una pantalla de scroll para nada.
 */
export function buildCalendarGrid(
  monthKey: string,
  events: readonly StoreEvent[],
  now: Date = new Date(),
): CalendarDay[][] {
  const [yearRaw, monthRaw] = monthKey.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return [];
  }

  const byDay = groupEventsByDay(events);
  const today = todayKey(now);

  const firstOfMonth = new Date(Date.UTC(year, month - 1, 1));
  const lastOfMonth = new Date(Date.UTC(year, month, 0));

  const cursor = new Date(firstOfMonth);
  cursor.setUTCDate(cursor.getUTCDate() - mondayFirstIndex(firstOfMonth));

  // Relleno final hasta completar la semana del último día del mes.
  const end = new Date(lastOfMonth);
  end.setUTCDate(end.getUTCDate() + (DAYS_PER_WEEK - 1 - mondayFirstIndex(lastOfMonth)));

  const weeks: CalendarDay[][] = [];
  let week: CalendarDay[] = [];

  while (cursor <= end) {
    const key = dayKeyOf(cursor);
    week.push({
      key,
      dayOfMonth: cursor.getUTCDate(),
      inMonth: cursor.getUTCMonth() === month - 1 && cursor.getUTCFullYear() === year,
      isToday: key === today,
      events: byDay.get(key) ?? [],
    });

    if (week.length === DAYS_PER_WEEK) {
      weeks.push(week);
      week = [];
    }

    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return weeks;
}
